<template>
    <div>
        <h4 class="m-b-10">
            {{ $t("amas:analysis:pra:table_title") }}
            <small v-if="pending" class="text-muted m-l-10"><i class="fa fa-spinner fa-spin m-r-5"></i>{{ $t("amas:analysis:pra:updating_route") }}</small>
        </h4>
        <p v-if="!hasTrace" class="text-warning">
            <i class="fa fa-exclamation-triangle"></i> {{ $t("amas:analysis:pra:no_trace_hint") }}
        </p>
        <table class="table table-hover pra-trace-table">
            <thead>
                <tr>
                    <th>{{ $t("amas:analysis:pra:col_parcel") }}</th>
                    <th class="text-center">{{ $t("amas:analysis:pra:state_road") }}</th>
                    <th class="text-center">{{ $t("amas:analysis:pra:col_legal") }}</th>
                    <th class="text-center">{{ $t("amas:analysis:pra:col_physical") }}</th>
                    <th></th>
                    <th></th>
                </tr>
            </thead>
            <tbody v-for="group in groups" :key="group.key">
                <tr class="pra-group-row">
                    <td colspan="6">
                        <span class="font-medium">{{ group.title }}</span>
                        <span v-if="group.subtitle" class="text-muted m-l-10">{{ group.subtitle }}</span>
                        <button v-if="group.collapsible" type="button" class="btn btn-xs btn-outline btn-default pull-right"
                            @click="toggle(group.key)">
                            {{ isOpen(group) ? $t("amas:analysis:pra:hide_parcels") : $t("amas:analysis:pra:show_parcels") }}
                        </button>
                    </td>
                </tr>
                <template v-if="isOpen(group)">
                    <trace-row
                        v-for="entry in group.entries"
                        :key="group.key + '-' + entry.property_id"
                        :entry="entry"
                        :highlight="highlightedPropertyId === entry.property_id"
                        :zk-loading="!!zkLoading[entry.property_id]"
                        :pending="pending"
                        @mouseover="$emit('highlight', $event)"
                        @mouseout="$emit('highlight', null)"
                        @remove-step="$emit('remove-step', $event)"
                        @zk-extract="$emit('zk-extract', $event)"
                        @override="(pid, field, value) => $emit('override', pid, field, value)"
                    />
                </template>
            </tbody>
        </table>
    </div>
</template>

<script>
import TraceRow from "./TraceRow.vue";

export default {
    components: { TraceRow },
    props: {
        entries: { type: Array, required: true },
        highlightedPropertyId: { type: String, default: null },
        hasTrace: { type: Boolean, default: true },
        zkLoading: { type: Object, default: () => ({}) },
        pending: { type: Boolean, default: false },
    },
    data() { return { open: {} }; },
    methods: {
        toggle(key) { this.$set(this.open, key, !this.isOpen({ key: key, collapsible: true, defaultOpen: false })); },
        isOpen(group) {
            if (!group.collapsible) { return true; }
            return this.open[group.key] === undefined ? group.defaultOpen : this.open[group.key];
        },
        deeperSubtitle(deeper) {
            if (deeper.some(e => e.state !== "off_route")) { return ""; }
            return this.$t("amas:analysis:pra:group_deeper_summary:{}").replace("{}", deeper.length);
        },
    },
    computed: {
        groups() {
            const rank = e => (e.state !== "off_route" ? 1 : 0);
            const byPath = (a, b) => rank(b) - rank(a);
            const subject = this.entries.filter(e => e.depth === 0);
            const step1 = this.entries.filter(e => e.depth === 1).sort(byPath);
            const deeper = this.entries.filter(e => e.depth > 1).sort(byPath);
            const manual = this.entries.filter(e => e.manual && !e.depth);
            const groups = [];
            if (subject.length) {
                groups.push({ key: "subject", title: this.$t("amas:analysis:pra:group_subject"), entries: subject, collapsible: false });
            }
            if (step1.length) {
                groups.push({ key: "step1", title: this.$t("amas:analysis:pra:group_step:{}").replace("{}", "1") + " (" + step1.length + ")", entries: step1, collapsible: false });
            }
            if (deeper.length) {
                const maxDepth = Math.max.apply(null, deeper.map(e => e.depth));
                const via = deeper.map(e => e.via_property_id).filter((v, i, arr) => v && arr.indexOf(v) === i);
                const label = maxDepth > 2 ? "2-" + maxDepth : "2";
                groups.push({
                    key: "deeper",
                    title: this.$t("amas:analysis:pra:group_deeper:{}").replace("{}", label) + " · " + this.$t("amas:analysis:pra:group_deeper_via:{}").replace("{}", via.join(", ")),
                    subtitle: this.deeperSubtitle(deeper),
                    entries: deeper,
                    collapsible: true,
                    defaultOpen: deeper.some(e => e.state !== "off_route" || e.overridden),
                });
            }
            if (manual.length) {
                groups.push({ key: "manual", title: this.$t("amas:analysis:pra:group_manual"), entries: manual, collapsible: false });
            }
            return groups;
        },
    },
}
</script>

<style>
.pra-trace-table .pra-group-row > td { background-color: #f7f8fa; border-top: 2px solid #e3e7ec; }
.pra-trace-table td { vertical-align: top !important; }
.pra-trace-table .pra-group-row > td { vertical-align: middle !important; }
</style>
